import Seo from "../../components/seo";
import Layout from "../../components/layout";
import Header from "../../components/header";
import Footer from "../../components/footer";
import ServiceCTA from "../../components/ServiceCTA";
import ServiceHero from "../../components/ServiceHero";
import SocPartners from "../../components/socialPartners";
import { CardColumn, HorizontalScroll, LogoSlider, InfoCard } from "../../components/sections";
import { Link } from "react-router-dom";
import { FiBarChart2, FiZap, FiTrendingUp, FiUsers } from "react-icons/fi";
import { usePageMetadata } from "../../hooks/usePageMetadata";

const MediaTechnologyPage = () => {
  usePageMetadata({
    title: 'Media Technology - Bercastle',
    description: 'AI-powered performance marketing, campaign optimization, and data analytics for media companies.',
    pageType: 'service',
    content: {
      service: {
        name: "Media Technology",
        tagline: "AI-Powered Marketing Solutions",
        description: "We combine artificial intelligence with performance marketing expertise to deliver campaigns that drive measurable results across all channels.",
        url: "/services/media-technology"
      },
      capabilities: [
        {
          name: "Performance Marketing",
          description: "Data-driven campaigns optimized by AI for maximum ROI. From paid search and social to programmatic advertising."
        },
        {
          name: "Campaign Optimization",
          description: "AI-powered continuous optimization of ad creative, targeting, and bidding strategies. Real-time adjustments ensure peak performance."
        },
        {
          name: "Data Analytics",
          description: "Advanced analytics and reporting that provide actionable insights. Our AI systems identify patterns and opportunities."
        },
        {
          name: "Audience Targeting",
          description: "Precision targeting powered by machine learning. We identify and reach your ideal customers across multiple platforms."
        }
      ],
      platforms: ["Facebook & Instagram", "TikTok", "LinkedIn", "Twitter/X", "YouTube", "Pinterest"],
      value_propositions: [
        "AI-driven campaign optimization",
        "Real-time performance tracking",
        "Multi-channel expertise",
        "Data-driven decision making",
        "Proven ROI improvements",
        "Transparent reporting"
      ],
      meta: {
        source_url: "https://bercastle.com/services/media-technology",
        service_category: "Media Technology"
      }
    }
  });
  const mediaCapabilities = [
    {
      icon: FiBarChart2,
      title: "Performance Marketing",
      description: "Data-driven campaigns optimized by AI for maximum ROI. From paid search and social to programmatic advertising, we deliver measurable results across all channels."
    },
    {
      icon: FiZap,
      title: "Campaign Optimization",
      description: "AI-powered continuous optimization of ad creative, targeting, and bidding strategies. Real-time adjustments ensure peak performance throughout your campaign lifecycle."
    },
    {
      icon: FiTrendingUp,
      title: "Data Analytics",
      description: "Advanced analytics and reporting that provide actionable insights. Our AI systems identify patterns and opportunities that drive strategic decision-making."
    },
    {
      icon: FiUsers,
      title: "Audience Targeting",
      description: "Precision targeting powered by machine learning. We identify and reach your ideal customers across multiple touchpoints with personalized messaging."
    }
  ];

  const aiStats = [
    {
      title: "AI",
      description: "Optimized Campaigns"
    },
    {
      title: "Real-Time",
      description: "Performance Data"
    },
    {
      title: "Expert",
      description: "Strategy Team"
    },
    {
      title: "Proven",
      description: "Results"
    }
  ];

  const technologyStack = [
    {
      icon: FiZap,
      title: "AI-Driven Insights",
      description: "Machine learning algorithms that predict trends and optimize performance in real-time"
    },
    {
      icon: FiZap,
      title: "Real-Time Optimization",
      description: "Automated bid management and creative rotation for continuous performance improvement"
    },
    {
      icon: FiBarChart2,
      title: "Unified Dashboard",
      description: "Comprehensive reporting across all channels with actionable insights and recommendations"
    }
  ];

  const technologyLogos = [
    { src: "/images/media/1.png", alt: "Logo 1" },
    { src: "/images/media/Frame 2.png", alt: "Logo 2" },
    { src: "/images/media/Frame 3.png", alt: "Logo 3" },
    { src: "/images/media/Frame 4.png", alt: "Logo 4" },
    { src: "/images/media/Frame 5.png", alt: "Logo 5" },
    { src: "/images/media/Frame 6.png", alt: "Logo 6" },
    { src: "/images/media/Frame 7.png", alt: "Logo 7" },
    { src: "/images/media/Frame 8.png", alt: "Logo 8" },
    { src: "/images/media/Frame 9.png", alt: "Logo 9" },
    { src: "/images/media/Frame 10.png", alt: "Logo 10" },
    { src: "/images/media/Frame 11.png", alt: "Logo 11" },
    { src: "/images/media/Frame 12.png", alt: "Logo 12" }
  ];
  return (
    <Layout classmain="media-technology-page">
      <Seo
        title='Media Technology - Bercastle'
        metaDesciption='AI-powered performance marketing, campaign optimization, and data analytics. Building technology that drives exceptional ROI and sustained growth.'
      />
      <Header/>

      <ServiceHero
        badge="Media Technology"
        title="AI-Powered Marketing"
        description="Bercastle is a premier independent agency founded on expertise and passion. Our team delivers impactful advertising solutions with AI-powered technology that drives exceptional campaign outcomes and sustained growth."
        ctaText="Launch Your Campaign"
        ctaLink="/contact"
      />

      {/* About Section */}
      <div className='py-20 md:py-28'>
        <div className='container mx-auto max-w-[1100px] px-4'>
          <div className='flex flex-wrap items-center -mx-4'>
            <div className='w-full md:w-1/2 px-4 mb-10 md:mb-0'>
              <h2 className='text-4xl md:text-5xl mb-6'>Media Excellence Powered by AI</h2>
              <div className='w-20 h-1 bg-brand-red mb-6'></div>
              <p className='text-lg text-gray-700 leading-relaxed mb-4'>
                As a premier independent agency, Bercastle combines traditional advertising expertise with cutting-edge AI technology. Our tight-knit team of talented professionals is committed to delivering impactful solutions that drive real business results.
              </p>
              <p className='text-lg text-gray-700 leading-relaxed'>
                Our ultimate objective is to deliver exceptional campaign outcomes and foster sustained growth for our valued clients. We leverage artificial intelligence to optimize every aspect of your marketing strategy, from audience targeting to creative optimization and performance analysis.
              </p>
            </div>
            <div className='w-full md:w-1/2 px-4'>
              <div className='grid grid-cols-2 gap-6'>
                {aiStats.map((stat, index) => (
                  <InfoCard key={index} title={stat.title}>
                    {stat.description}
                  </InfoCard>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Services Detail Section */}
      <div className='py-20 md:py-28'>
        <div className='container mx-auto max-w-[1100px] px-4'>
          <div className='text-center mb-16'>
            <h2 className='text-4xl md:text-5xl mb-6'>Our Media Capabilities</h2>
            <div className='w-20 h-1 bg-brand-red mx-auto mb-6'></div>
            <p className='text-lg text-gray-600 max-w-3xl mx-auto'>
              Comprehensive advertising solutions enhanced by artificial intelligence to maximize your ROI
            </p>
          </div>
          <CardColumn cards={mediaCapabilities} cols={2} />
        </div>
      </div>

      {/* Media Partners Section */}
      <div className='py-20 md:py-28'>
        <div className='container mx-auto max-w-[1100px] px-4'>
          <div className='text-center mb-16'>
            <h2 className='text-4xl md:text-5xl mb-6'>Media Partners</h2>
            <div className='w-20 h-1 bg-brand-red mx-auto mb-6'></div>
            <p className='text-lg text-gray-600 max-w-3xl mx-auto'>
              Trusted by leading brands across industries to deliver innovative AI and technology solutions
            </p>
          </div>
          <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8'>
            {technologyLogos.map((logo, index) => (
              <div key={index} className='flex items-center justify-center p-4'>
                <img src={logo.src} alt={logo.alt} className='h-16 object-contain' />
              </div>
            ))}
          </div>
        </div>
      </div>

      <ServiceCTA 
        title="Ready to Transform Your Marketing?"
        description="Let's discuss how our AI-powered media technology can drive exceptional results for your business."
        buttonText="Get Started"
      />

      <Footer/>
    </Layout>
  )
}

export default MediaTechnologyPage
